import clsx from 'clsx';
import type { PropsWithChildren } from 'react';
import styles from './Typography.module.css';
import { type PolymorphicProps, type TypographyProps } from './types';

export function getTypographyStyles({
  size,
  height,
  color,
  weight,
  family,
  transform,
  whiteSpace,
  textWrap,
  wordWrap,
  align,
  decoration,
  fluid,
}: Omit<TypographyProps, 'truncate'>) {
  return clsx(
    styles.root,
    fluid && styles.fluid,
    styles[`height-${height}`],
    styles[`color-${color}`],
    styles[`weight-${weight}`],
    styles[`size-${size}`],
    styles[`family-${family}`],
    styles[`transform-${transform}`],
    styles[`decoration-${decoration}`],
    styles[`text-align-${align}`],
    styles[`white-space-${whiteSpace}`],
    styles[`text-wrap-${textWrap}`],
    styles[`word-wrap-${wordWrap}`],
  );
}

export function getTruncationStyles({ truncate }: Pick<TypographyProps, 'truncate'>) {
  return clsx({
    [styles.truncate]: truncate,
  });
}

/**
 * This utilily helps us not to forget to filter out any new typography property
 */
const objectOfAll =
  <T>() =>
  <U extends Record<keyof T, any>>(
    obj: U &
      // ❌ Missing keys check
      ([keyof T] extends [keyof U]
        ? // ❌ Extra keys check
          [keyof U] extends [keyof T]
          ? unknown
          : 'Extra keys'
        : 'Missing keys'),
  ) =>
    obj as T;

export function filterTypographyProps(props: PolymorphicProps<TypographyProps>): {
  typographyProps: TypographyProps;
  restProps: PolymorphicProps<object>;
};

export function filterTypographyProps(props: PropsWithChildren<TypographyProps>): {
  typographyProps: TypographyProps;
  restProps: PropsWithChildren;
};

export function filterTypographyProps({
  size,
  height,
  color,
  weight,
  family,
  transform,
  truncate,
  whiteSpace,
  textWrap,
  wordWrap,
  align,
  decoration,
  fluid,
  ...rest
}: PolymorphicProps<TypographyProps> | PropsWithChildren<TypographyProps>) {
  const typographyProps = objectOfAll<TypographyProps>()({
    size,
    height,
    color,
    weight,
    family,
    transform,
    truncate,
    whiteSpace,
    textWrap,
    wordWrap,
    align,
    decoration,
    fluid,
  });
  return {
    typographyProps,
    restProps: rest,
  };
}
